// @ts-nocheck

/**
 * MSI3 Revenue Calculation Service
 * 
 * Handles revenue share calculations, inactivity monitoring,
 * and 90-day lookback transfer logic.
 */

import { db } from '../firebase';
import firebase from 'firebase/compat/app';
import { MSI3_CONSTANTS } from './constants';
import { marketPenetrationService } from './marketPenetrationService';

const { REVENUE_STATUS, PRICING } = MSI3_CONSTANTS;

export const revenueCalculationService = {
  /**
   * Calculate monthly revenue for a patient enrollment
   * @param {string} subscriptionTier - free, small, medium, large
   * @param {number} revenueSharePct - 75, 50, or 25
   * @param {string} pricingTier - 'TIER_1', 'TIER_2', 'TIER_3', 'TIER_4'
   * @returns {number} Monthly revenue amount in USD
   */
  calculateMonthlyRevenue(subscriptionTier, revenueSharePct, pricingTier = 'TIER_1') {
    const annualPricing = PRICING[pricingTier] || PRICING.TIER_1;
    const annualAmount = annualPricing[subscriptionTier] || 0;
    const monthlySubscription = annualAmount / 12;
    const monthlyRevenue = monthlySubscription * (revenueSharePct / 100);
    return monthlyRevenue;
  },

  /**
   * Get geographic pricing tier for a country
   * @param {string} countryCode - ISO 2-letter country code
   * @returns {string} Pricing tier key
   */
  getPricingTierForCountry(countryCode) {
    const tier1Countries = ['KE', 'NG', 'GH', 'TZ', 'UG', 'RW', 'ET', 'ZA', 'EG']; // Africa
    const tier2Countries = ['IN', 'PK', 'BD', 'BR', 'MX', 'AR', 'CO', 'PH', 'ID', 'VN', 'TH']; // Growth markets
    const tier3Countries = ['GB', 'DE', 'FR', 'IT', 'ES', 'JP', 'KR', 'NL', 'BE', 'SE', 'NO']; // Europe/Asia
    const tier4Countries = ['US', 'CA', 'AU', 'CH', 'NZ', 'IE', 'SG']; // Premium

    if (tier1Countries.includes(countryCode)) return 'TIER_1';
    if (tier2Countries.includes(countryCode)) return 'TIER_2';
    if (tier3Countries.includes(countryCode)) return 'TIER_3';
    if (tier4Countries.includes(countryCode)) return 'TIER_4';

    // Default to TIER_2 for unknown countries
    return 'TIER_2';
  },

  /**
   * Check all enrollments for inactivity and handle transfers
   * This should be run as a daily scheduled job
   */
  async checkAllInactivityAndTransfers() {
    const activeEnrollments = await db
      .collection('msi3PatientEnrollments')
      .where('revenueShareStatus', '==', REVENUE_STATUS.ACTIVE)
      .get();

    const results = {
      checked: 0,
      paused: 0,
      transferred: 0,
      errors: [],
    };

    for (const doc of activeEnrollments.docs) {
      results.checked++;
      const enrollment = { id: doc.id, ...doc.data() };

      try {
        const result = await this.checkInactivityForEnrollment(enrollment);
        if (result.paused) results.paused++;
        if (result.transferred) results.transferred++;
      } catch (error) {
        results.errors.push({
          enrollmentId: enrollment.id,
          error: error.message,
        });
      }
    }

    return results;
  },

  /**
   * Check inactivity for a single enrollment
   */
  async checkInactivityForEnrollment(enrollment) {
    const now = new Date();
    const lastContactDate = enrollment.lastClinicalContactDate?.toDate
      ? enrollment.lastClinicalContactDate.toDate()
      : new Date(enrollment.lastClinicalContactDate);

    const protectionDays = (enrollment.inactivityProtectionMonths || 18) * 30;
    const daysSinceContact = Math.floor((now - lastContactDate) / (1000 * 60 * 60 * 24));

    if (daysSinceContact > protectionDays) {
      // Protection expired - check for 90-day lookback transfer
      return await this.handleProtectionExpired(enrollment, lastContactDate);
    }

    return { paused: false, transferred: false };
  },

  /**
   * Handle expired protection - check for lookback transfer or pause
   */
  async handleProtectionExpired(enrollment, lastContactDate) {
    const protectionMonths = enrollment.inactivityProtectionMonths || 18;
    const protectionEndDate = new Date(lastContactDate);
    protectionEndDate.setMonth(protectionEndDate.getMonth() + protectionMonths);

    const lookbackStartDate = new Date(protectionEndDate);
    lookbackStartDate.setDate(lookbackStartDate.getDate() - MSI3_CONSTANTS.LOOKBACK_DAYS);

    // Find contacts with OTHER facilities during lookback window
    const otherContacts = await db
      .collection('msi3ClinicalContacts')
      .where('patientUserId', '==', enrollment.patientUserId)
      .where('contactDate', '>=', lookbackStartDate)
      .where('contactDate', '<=', protectionEndDate)
      .orderBy('contactDate', 'desc')
      .get();

    // Filter to contacts with different facilities
    const contactsWithOthers = otherContacts.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((contact) => contact.facilityUserId !== enrollment.currentRevenueFacilityId);

    if (contactsWithOthers.length > 0) {
      // Transfer to most recent other facility
      return await this.transferRevenueShare(enrollment, contactsWithOthers[0]);
    } else {
      // No other contacts - pause revenue share
      await db.collection('msi3PatientEnrollments').doc(enrollment.id).update({
        revenueShareStatus: REVENUE_STATUS.PAUSED,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });

      return { paused: true, transferred: false };
    }
  },

  /**
   * Transfer revenue share to a new facility
   */
  async transferRevenueShare(enrollment, newContact) {
    // Get new facility partner record
    const newFacilitySnapshot = await db
      .collection('msi3FacilityPartners')
      .where('facilityUserId', '==', newContact.facilityUserId)
      .limit(1)
      .get();

    if (newFacilitySnapshot.empty) {
      // New facility is not an MSI3 partner - pause instead
      await db.collection('msi3PatientEnrollments').doc(enrollment.id).update({
        revenueShareStatus: REVENUE_STATUS.PAUSED,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      return { paused: true, transferred: false };
    }

    const newFacility = { id: newFacilitySnapshot.docs[0].id, ...newFacilitySnapshot.docs[0].data() };

    // Calculate NEW revenue share based on current market penetration
    const currentPenetration = await marketPenetrationService.calculatePenetration(newFacility.cityId);
    const newRevenueSharePct = this.getRevenueSharePercentage(currentPenetration);
    const newMonthlyAmount = this.calculateMonthlyRevenue(
      enrollment.subscriptionTier,
      newRevenueSharePct,
      'TIER_1' // TODO: Get from city/country
    );

    // Update enrollment record with transfer
    await db.collection('msi3PatientEnrollments').doc(enrollment.id).update({
      currentRevenueFacilityId: newFacility.id,
      lockedRevenueSharePercentage: newRevenueSharePct,
      lockedMonthlyAmount: newMonthlyAmount,
      revenueShareStatus: REVENUE_STATUS.ACTIVE,
      lastClinicalContactDate: newContact.contactDate,
      inactivityProtectionMonths: newFacility.earlyAdopterProtectionMonths || MSI3_CONSTANTS.STANDARD_PROTECTION_MONTHS,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    // Log the transfer
    await db.collection('msi3RevenueTransfers').add({
      patientUserId: enrollment.patientUserId,
      fromFacilityId: enrollment.currentRevenueFacilityId,
      toFacilityId: newFacility.id,
      triggerContactId: newContact.id,
      oldRevenueSharePct: enrollment.lockedRevenueSharePercentage,
      newRevenueSharePct,
      oldMonthlyAmount: enrollment.lockedMonthlyAmount,
      newMonthlyAmount,
      transferredAt: firebase.firestore.FieldValue.serverTimestamp(),
    });


    return { paused: false, transferred: true };
  },

  /**
   * Get revenue share percentage based on market penetration
   */
  getRevenueSharePercentage(penetration) {
    const { REVENUE_TIERS } = MSI3_CONSTANTS;
    if (penetration <= REVENUE_TIERS.LOW_PENETRATION.maxPercent) {
      return REVENUE_TIERS.LOW_PENETRATION.revenueShare;
    }
    if (penetration <= REVENUE_TIERS.MEDIUM_PENETRATION.maxPercent) {
      return REVENUE_TIERS.MEDIUM_PENETRATION.revenueShare;
    }
    return REVENUE_TIERS.HIGH_PENETRATION.revenueShare;
  },

  /**
   * Generate monthly revenue report for a facility
   * @param {string} facilityPartnerId - Facility partnership ID
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @returns {Promise<Object>} Report data
   */
  async generateMonthlyReport(facilityPartnerId, year, month) {
    const facility = await db.collection('msi3FacilityPartners').doc(facilityPartnerId).get();
    if (!facility.exists) return null;

    const facilityData = facility.data();

    // Get city data
    const city = await db.collection('msi3PopulationCenters').doc(facilityData.cityId).get();
    const cityData = city.exists ? city.data() : {};

    // Get all patients where this facility earns revenue
    const enrollmentsSnapshot = await db
      .collection('msi3PatientEnrollments')
      .where('currentRevenueFacilityId', '==', facilityPartnerId)
      .where('revenueShareStatus', '==', REVENUE_STATUS.ACTIVE)
      .get();

    const enrollments = enrollmentsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Calculate statistics
    let totalRevenue = 0;
    let inMarketCount = 0;
    let outOfMarketCount = 0;
    const tierBreakdown = { free: 0, small: 0, medium: 0, large: 0 };

    const patientDetails = enrollments.map((enrollment) => {
      const daysActive = this.calculateDaysActiveInMonth(enrollment, year, month);
      const amountDue = (enrollment.lockedMonthlyAmount / 30) * daysActive;
      totalRevenue += amountDue;

      if (enrollment.countsTowardMarketPenetration) {
        inMarketCount++;
      } else {
        outOfMarketCount++;
      }

      tierBreakdown[enrollment.subscriptionTier]++;

      return {
        patientId: enrollment.patientUserId,
        subscriptionTier: enrollment.subscriptionTier,
        enrollmentDate: enrollment.enrollmentDate,
        lockedRevenueShare: enrollment.lockedRevenueSharePercentage,
        monthlyAmount: enrollment.lockedMonthlyAmount,
        inMarket: enrollment.countsTowardMarketPenetration,
        daysActive,
        amountDue,
      };
    });

    return {
      facilityId: facilityPartnerId,
      facilityName: facilityData.facilityName,
      cityName: cityData.cityName,
      period: { year, month },
      generatedAt: new Date(),
      summary: {
        totalPatients: enrollments.length,
        inMarketPatients: inMarketCount,
        outOfMarketPatients: outOfMarketCount,
        tierBreakdown,
        totalRevenue,
      },
      patients: patientDetails,
    };
  },

  /**
   * Calculate days a patient was active in a specific month
   */
  calculateDaysActiveInMonth(enrollment, year, month) {
    const enrollmentDate = enrollment.enrollmentDate?.toDate
      ? enrollment.enrollmentDate.toDate()
      : new Date(enrollment.enrollmentDate);

    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);

    const activeStart = enrollmentDate > monthStart ? enrollmentDate : monthStart;
    const activeEnd = monthEnd;

    if (activeStart > activeEnd) return 0;

    const daysActive = Math.ceil((activeEnd - activeStart) / (1000 * 60 * 60 * 24)) + 1;
    return Math.min(daysActive, monthEnd.getDate());
  },
};

